import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityScopes } from '@4gaboards/enums';
import locales from '@4gaboards/locales';
import { useReactTable, getCoreRowModel, getGroupedRowModel, getExpandedRowModel, flexRender } from '@tanstack/react-table';
import clsx from 'clsx';
import uniq from 'lodash/uniq';
import PropTypes from 'prop-types';

import { ThemeShapes, Themes, NotificationsDeliveryModes } from '../../../constants/Enums';
import { useForm } from '../../../hooks';
import { Table } from '../../Utils';
import CustomThemeButton from './CustomThemeButton';
import NotificationTypesSelector from './NotificationTypesSelector';
import ThemeShapeSelector from './ThemeShapeSelector';

import * as gs from '../../../global.module.scss';
import * as sShared from '../SettingsShared.module.scss';
import * as s from './PreferencesSettings.module.scss';

const PreferencesSettings = React.memo(
  ({
    isAdmin,
    isVerified,
    subscribeToOwnCards,
    subscribeToNewBoards,
    subscribeToNewProjects,
    subscribeToUsers,
    subscribeToInstance,
    sidebarCompact,
    language,
    defaultView,
    listViewStyle,
    usersSettingsStyle,
    preferredDetailsFont,
    hideCardModalActivity,
    hideClosestDueDate,
    showFullDueDates,
    theme,
    themeShape,
    onUpdate,
    emailNotificationsEnabled,
    emailNotificationsTypes,
    emailNotificationsDeliveryMode,
    emailNotificationsMarkReadAsDelivered,
    notificationTypes,
    suppressedSystemNotificationTags,
  }) => {
    const [t] = useTranslation();
    const tableRef = useRef(null);

    const Groups = {
      NOTIFICATIONS: 'notifications',
      GENERAL: 'general',
      EMAIL_NOTIFICATIONS: 'emailNotifications',
    };

    const languages = useMemo(
      () => [
        {
          id: 'auto',
          name: t('common.detectAutomatically'),
        },
        ...locales.map((locale) => ({
          id: locale.language,
          flags: locale.flags,
          name: locale.name,
        })),
      ],
      [t],
    );
    const selectedLanguage = useMemo(() => languages.find((lang) => lang.id === (language || 'auto')), [languages, language]);

    const defaultViews = useMemo(
      () => [
        {
          id: 'list',
          name: t('common.listView'),
        },
        {
          id: 'board',
          name: t('common.boardView'),
        },
      ],
      [t],
    );
    const selectedDefaultView = useMemo(() => defaultViews.find((view) => view.id === defaultView), [defaultViews, defaultView]);

    const listStyles = useMemo(
      () => [
        {
          id: 'default',
          name: t('common.default'),
        },
        {
          id: 'compact',
          name: t('common.compact'),
        },
      ],
      [t],
    );

    const selectedListViewStyle = useMemo(() => listStyles.find((style) => style.id === listViewStyle), [listStyles, listViewStyle]);

    const selectedUsersSettingsStyle = useMemo(() => listStyles.find((style) => style.id === usersSettingsStyle), [listStyles, usersSettingsStyle]);

    const preferredFonts = useMemo(
      () => [
        {
          id: 'default',
          name: t('common.default'),
        },
        {
          id: 'monospace',
          name: t('common.monospace'),
        },
      ],
      [t],
    );

    const selectedPreferredDetailsFont = useMemo(() => preferredFonts.find((style) => style.id === preferredDetailsFont), [preferredDetailsFont, preferredFonts]);

    const themes = useMemo(
      () => [
        {
          id: Themes.DEFAULT,
          name: t('common.themeDefault'),
        },
        {
          id: Themes.GITHUB_DARK,
          name: t('common.themeGithubDark'),
          badge: t('common.beta'),
        },
        {
          id: Themes.LIGHT,
          name: t('common.themeLight'),
          badge: t('common.beta'),
        },
        {
          id: Themes.CUSTOM,
          name: t('common.themeCustom'),
          badge: t('common.beta'),
        },
      ],
      [t],
    );

    const selectedTheme = useMemo(() => themes.find((th) => th.id === theme), [themes, theme]);

    const themeShapes = useMemo(
      () => [
        {
          id: ThemeShapes.DEFAULT,
          name: t('common.themeShapeDefault'),
        },
        {
          id: ThemeShapes.ROUNDED,
          name: t('common.themeShapeRounded'),
        },
      ],
      [t],
    );

    const selectedThemeShape = useMemo(() => themeShapes.find((shape) => shape.id === themeShape), [themeShapes, themeShape]);

    const notificationTypesOptions = useMemo(
      () => [
        {
          id: ActivityScopes.INSTANCE,
          name: t('activity.instanceShort'),
        },
        {
          id: ActivityScopes.USER,
          name: t('activity.userShort'),
        },
        {
          id: ActivityScopes.PROJECT,
          name: t('activity.projectShort'),
        },
        {
          id: ActivityScopes.BOARD,
          name: t('activity.boardShort'),
        },
        {
          id: ActivityScopes.LIST,
          name: t('activity.listShort'),
        },
        {
          id: ActivityScopes.CARD,
          name: t('activity.cardShort'),
        },
        {
          id: ActivityScopes.TASK,
          name: t('activity.taskShort'),
        },
        {
          id: ActivityScopes.COMMENT,
          name: t('activity.commentShort'),
        },
        {
          id: ActivityScopes.ATTACHMENT,
          name: t('common.attachment'),
        },
      ],
      [t],
    );

    const selectedNotificationTypes = useMemo(() => notificationTypesOptions.filter((opt) => notificationTypes.includes(opt.id)), [notificationTypesOptions, notificationTypes]);

    const [suppressedSystemNotificationTagsData, handleSuppressedSystemNotificationTagsFieldChange] = useForm(() => ({
      text: suppressedSystemNotificationTags.join(', '),
    }));

    useEffect(() => {
      handleSuppressedSystemNotificationTagsFieldChange({ target: { name: 'text', value: suppressedSystemNotificationTags.join(', ') } });
    }, [suppressedSystemNotificationTags, handleSuppressedSystemNotificationTagsFieldChange]);

    const selectedEmailNotificationsTypes = useMemo(() => notificationTypesOptions.filter((opt) => emailNotificationsTypes.includes(opt.id)), [notificationTypesOptions, emailNotificationsTypes]);

    const emailNotificationsDeliveryModes = useMemo(
      () => [
        {
          id: NotificationsDeliveryModes.INSTANT,
          name: t('common.emailNotificationsDeliveryModeInstant'),
        },
        {
          id: NotificationsDeliveryModes.BATCHED,
          name: t('common.emailNotificationsDeliveryModeBatched'),
        },
        {
          id: NotificationsDeliveryModes.INSTANT_THEN_BATCHED,
          name: t('common.emailNotificationsDeliveryModeInstantThenBatched'),
        },
      ],
      [t],
    );

    const selectedEmailNotificationsDeliveryMode = useMemo(
      () => emailNotificationsDeliveryModes.find((deliveryMode) => deliveryMode.id === emailNotificationsDeliveryMode),
      [emailNotificationsDeliveryMode, emailNotificationsDeliveryModes],
    );

    const handleSubscribeToOwnCardsChange = useCallback(() => {
      onUpdate({
        subscribeToOwnCards: !subscribeToOwnCards,
      });
    }, [subscribeToOwnCards, onUpdate]);

    const handleSubscribeToNewBoardsChange = useCallback(() => {
      onUpdate({
        subscribeToNewBoards: !subscribeToNewBoards,
      });
    }, [subscribeToNewBoards, onUpdate]);

    const handleSubscribeToNewProjectsChange = useCallback(() => {
      onUpdate({
        subscribeToNewProjects: !subscribeToNewProjects,
      });
    }, [subscribeToNewProjects, onUpdate]);

    const handleSubscribeToUsersChange = useCallback(() => {
      onUpdate({
        subscribeToUsers: !subscribeToUsers,
      });
    }, [subscribeToUsers, onUpdate]);

    const handleSubscribeToInstanceChange = useCallback(() => {
      onUpdate({
        subscribeToInstance: !subscribeToInstance,
      });
    }, [subscribeToInstance, onUpdate]);

    const handleCompactSidebarChange = useCallback(() => {
      onUpdate({
        sidebarCompact: !sidebarCompact,
      });
    }, [onUpdate, sidebarCompact]);

    const handleDefaultViewChange = useCallback(
      (value) => {
        onUpdate({ defaultView: value.id });
      },
      [onUpdate],
    );

    const handleListViewStyleChange = useCallback(
      (value) => {
        onUpdate({ listViewStyle: value.id });
      },
      [onUpdate],
    );

    const handleUsersSettingsStyleChange = useCallback(
      (value) => {
        onUpdate({ usersSettingsStyle: value.id });
      },
      [onUpdate],
    );

    const handlePreferredDetailsFontChange = useCallback(
      (value) => {
        onUpdate({ preferredDetailsFont: value.id });
      },
      [onUpdate],
    );

    const handleHideCardModalActivityChange = useCallback(() => {
      onUpdate({
        hideCardModalActivity: !hideCardModalActivity,
      });
    }, [onUpdate, hideCardModalActivity]);

    const handleHideClosestDueDateChange = useCallback(() => {
      onUpdate({
        hideClosestDueDate: !hideClosestDueDate,
      });
    }, [onUpdate, hideClosestDueDate]);

    const handleShowFullDueDatesChange = useCallback(() => {
      onUpdate({
        showFullDueDates: !showFullDueDates,
      });
    }, [onUpdate, showFullDueDates]);

    const handleLanguageChange = useCallback(
      (value) => {
        onUpdate({ language: value.id === 'auto' ? null : value.id }); // FIXME: hack
      },
      [onUpdate],
    );

    const handleThemeChange = useCallback(
      (value) => {
        onUpdate({ theme: value.id });
      },
      [onUpdate],
    );

    const handleThemeShapeChange = useCallback(
      (value) => {
        onUpdate({ themeShape: value.id });
      },
      [onUpdate],
    );

    const handleEmailNotificationsEnabledChange = useCallback(() => {
      onUpdate({
        emailNotificationsEnabled: !emailNotificationsEnabled,
      });
    }, [onUpdate, emailNotificationsEnabled]);

    const handleNotificationTypesChange = useCallback(
      (value) => {
        const { id } = value;

        const next = notificationTypes.includes(id) ? notificationTypes.filter((type) => type !== id) : [...notificationTypes, id];

        onUpdate({ notificationTypes: next });
      },
      [onUpdate, notificationTypes],
    );

    const handleSuppressedSystemNotificationTagsSubmit = useCallback(
      (e) => {
        const next = uniq(
          e.target.value
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean),
        );

        if (next.join(',') !== suppressedSystemNotificationTags.join(',')) {
          onUpdate({ suppressedSystemNotificationTags: next });
        }

        handleSuppressedSystemNotificationTagsFieldChange({ target: { name: 'text', value: next.join(', ') } });
      },
      [handleSuppressedSystemNotificationTagsFieldChange, onUpdate, suppressedSystemNotificationTags],
    );

    const handleEmailNotificationsTypesChange = useCallback(
      (value) => {
        const { id } = value;

        const next = emailNotificationsTypes.includes(id) ? emailNotificationsTypes.filter((type) => type !== id) : [...emailNotificationsTypes, id];

        onUpdate({ emailNotificationsTypes: next });
      },
      [onUpdate, emailNotificationsTypes],
    );

    const handleEmailNotificationsDeliveryModeChange = useCallback(
      (value) => {
        onUpdate({ emailNotificationsDeliveryMode: value.id });
      },
      [onUpdate],
    );

    const handleEmailNotificationsMarkReadAsDeliveredChange = useCallback(() => {
      onUpdate({
        emailNotificationsMarkReadAsDelivered: !emailNotificationsMarkReadAsDelivered,
      });
    }, [onUpdate, emailNotificationsMarkReadAsDelivered]);

    const data = useMemo(
      () =>
        [
          {
            id: 'language',
            preferences: t('common.language', { context: 'title' }),
            modifySettings: selectedLanguage,
            modifySettingsProps: { onChange: handleLanguageChange, options: languages, placeholder: selectedLanguage.name, isSearchable: true, selectFirstOnSearch: true },
            currentValue: selectedLanguage.name,
            description: t('common.descriptionLanguage'),
            group: Groups.GENERAL,
          },
          {
            id: 'theme',
            preferences: t('common.theme'),
            modifySettings: selectedTheme,
            modifySettingsProps: {
              onChange: handleThemeChange,
              options: themes,
              placeholder: selectedTheme.name,
              isSearchable: true,
              selectFirstOnSearch: true,
            },
            currentValue: selectedTheme.name,
            description: t('common.descriptionTheme'),
            group: Groups.GENERAL,
          },
          theme === Themes.CUSTOM && {
            id: 'customTheme',
            preferences: t('common.customTheme'),
            modifySettings: selectedTheme,
            modifySettingsProps: {
              isCustomComponent: true,
              CustomComponent: CustomThemeButton,
            },
            currentValue: t('common.customized'),
            description: t('common.descriptionCustomTheme'),
            group: Groups.GENERAL,
          },
          {
            id: 'themeShape',
            preferences: t('common.themeShape'),
            modifySettings: selectedThemeShape,
            modifySettingsProps: {
              isCustomComponent: true,
              CustomComponent: ThemeShapeSelector,
              onChange: handleThemeShapeChange,
              options: themeShapes,
            },
            currentValue: selectedThemeShape.name,
            description: t('common.descriptionThemeShape'),
            group: Groups.GENERAL,
          },
          {
            id: 'preferredDetailsFont',
            preferences: t('common.preferredDetailsFont'),
            modifySettings: selectedPreferredDetailsFont,
            modifySettingsProps: {
              onChange: handlePreferredDetailsFontChange,
              options: preferredFonts,
              placeholder: selectedPreferredDetailsFont.name,
              isSearchable: true,
              selectFirstOnSearch: true,
            },
            currentValue: selectedPreferredDetailsFont.name,
            description: t('common.descriptionPreferredDetailsFont'),
            group: Groups.GENERAL,
          },
          {
            id: 'defaultView',
            preferences: t('common.defaultView'),
            modifySettings: selectedDefaultView,
            modifySettingsProps: { onChange: handleDefaultViewChange, options: defaultViews, placeholder: selectedDefaultView.name, isSearchable: true, selectFirstOnSearch: true },
            currentValue: selectedDefaultView.name,
            description: t('common.descriptionDefaultView'),
            group: Groups.GENERAL,
          },
          {
            id: 'sidebarCompact',
            preferences: t('common.sidebarCompact'),
            modifySettings: sidebarCompact,
            modifySettingsProps: { onChange: handleCompactSidebarChange, title: t('common.toggleItem', { item: t('common.sidebarCompact', { postProcess: 'lowercase' }) }) },
            currentValue: sidebarCompact ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionCompactSidebar'),
            group: Groups.GENERAL,
          },
          {
            id: 'listViewStyle',
            preferences: t('common.listViewStyle'),
            modifySettings: selectedListViewStyle,
            modifySettingsProps: { onChange: handleListViewStyleChange, options: listStyles, placeholder: selectedListViewStyle.name, isSearchable: true, selectFirstOnSearch: true },
            currentValue: selectedListViewStyle.name,
            description: t('common.descriptionListViewStyle'),
            group: Groups.GENERAL,
          },
          {
            id: 'usersSettingsStyle',
            preferences: t('common.usersSettingsStyle'),
            modifySettings: selectedUsersSettingsStyle,
            modifySettingsProps: {
              onChange: handleUsersSettingsStyleChange,
              options: listStyles,
              placeholder: selectedUsersSettingsStyle.name,
              isSearchable: true,
              selectFirstOnSearch: true,
            },
            currentValue: selectedUsersSettingsStyle.name,
            description: t('common.descriptionUsersSettingsStyle'),
            group: Groups.GENERAL,
          },
          {
            id: 'hideCardModalActivity',
            preferences: t('common.hideCardModalActivity'),
            modifySettings: hideCardModalActivity,
            modifySettingsProps: { onChange: handleHideCardModalActivityChange, title: t('common.toggleItem', { item: t('common.hideCardModalActivity', { postProcess: 'lowercase' }) }) },
            currentValue: hideCardModalActivity ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionHideCardModalActivity'),
            group: Groups.GENERAL,
          },
          {
            id: 'hideClosestDueDate',
            preferences: t('common.hideClosestDueDate'),
            modifySettings: hideClosestDueDate,
            modifySettingsProps: { onChange: handleHideClosestDueDateChange, title: t('common.toggleItem', { item: t('common.hideClosestDueDate', { postProcess: 'lowercase' }) }) },
            currentValue: hideClosestDueDate ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionHideClosestDueDate'),
            group: Groups.GENERAL,
          },
          {
            id: 'showFullDueDates',
            preferences: t('common.showFullDueDates'),
            modifySettings: showFullDueDates,
            modifySettingsProps: { onChange: handleShowFullDueDatesChange, title: t('common.toggleItem', { item: t('common.showFullDueDates', { postProcess: 'lowercase' }) }) },
            currentValue: showFullDueDates ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionShowFullDueDates'),
            group: Groups.GENERAL,
          },
          {
            id: 'subscribeToOwnCards',
            preferences: t('common.subscribeToOwnCards'),
            modifySettings: subscribeToOwnCards,
            modifySettingsProps: { onChange: handleSubscribeToOwnCardsChange, title: t('common.toggleItem', { item: t('common.subscribeToOwnCards', { postProcess: 'lowercase' }) }) },
            currentValue: subscribeToOwnCards ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionSubscribeToOwnCards'),
            group: Groups.NOTIFICATIONS,
          },
          {
            id: 'subscribeToNewBoards',
            preferences: t('common.subscribeToNewBoards'),
            modifySettings: subscribeToNewBoards,
            modifySettingsProps: { onChange: handleSubscribeToNewBoardsChange, title: t('common.toggleItem', { item: t('common.subscribeToNewBoards', { postProcess: 'lowercase' }) }) },
            currentValue: subscribeToNewBoards ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionSubscribeToNewBoards'),
            group: Groups.NOTIFICATIONS,
          },
          {
            id: 'subscribeToNewProjects',
            preferences: t('common.subscribeToNewProjects'),
            modifySettings: subscribeToNewProjects,
            modifySettingsProps: { onChange: handleSubscribeToNewProjectsChange, title: t('common.toggleItem', { item: t('common.subscribeToNewProjects', { postProcess: 'lowercase' }) }) },
            currentValue: subscribeToNewProjects ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionSubscribeToNewProjects'),
            group: Groups.NOTIFICATIONS,
          },
          isAdmin && {
            id: 'subscribeToUsers',
            preferences: t('common.subscribeToUsers'),
            modifySettings: subscribeToUsers,
            modifySettingsProps: { onChange: handleSubscribeToUsersChange, title: t('common.toggleItem', { item: t('common.subscribeToUsers', { postProcess: 'lowercase' }) }) },
            currentValue: subscribeToUsers ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionSubscribeToUsers'),
            group: Groups.NOTIFICATIONS,
          },
          isAdmin && {
            id: 'subscribeToInstance',
            preferences: t('common.subscribeToInstance'),
            modifySettings: subscribeToInstance,
            modifySettingsProps: { onChange: handleSubscribeToInstanceChange, title: t('common.toggleItem', { item: t('common.subscribeToInstance', { postProcess: 'lowercase' }) }) },
            currentValue: subscribeToInstance ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionSubscribeToInstance'),
            group: Groups.NOTIFICATIONS,
          },
          {
            id: 'notificationTypes',
            preferences: t('common.notificationTypes'),
            modifySettings: selectedNotificationTypes,
            modifySettingsProps: {
              isCustomComponent: true,
              CustomComponent: NotificationTypesSelector,
              onChange: handleNotificationTypesChange,
              options: notificationTypesOptions,
            },
            currentValue: selectedNotificationTypes.map((v) => v.name).join(', '),
            description: t('common.descriptionNotificationTypes'),
            group: Groups.NOTIFICATIONS,
          },
          {
            id: 'suppressedSystemNotificationTags',
            preferences: t('common.suppressedSystemNotificationTags'),
            modifySettings: suppressedSystemNotificationTagsData.text,
            modifySettingsProps: {
              name: 'text',
              onSubmit: handleSuppressedSystemNotificationTagsSubmit,
              onChange: handleSuppressedSystemNotificationTagsFieldChange,
              placeholder: t('common.enterSystemNotificationTags'),
              title: t('common.suppressedSystemNotificationTags'),
            },
            currentValue: suppressedSystemNotificationTags.join(', '),
            description: t('common.descriptionSuppressedSystemNotificationTags'),
            group: Groups.NOTIFICATIONS,
          },
          {
            id: 'emailNotificationsEnabled',
            preferences: t('common.emailNotificationsEnabled'),
            modifySettings: emailNotificationsEnabled,
            modifySettingsProps: { onChange: handleEmailNotificationsEnabledChange, title: t('common.toggleItem', { item: t('common.emailNotificationsEnabled', { postProcess: 'lowercase' }) }) },
            currentValue: emailNotificationsEnabled ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionEmailNotificationsEnabled'),
            group: Groups.EMAIL_NOTIFICATIONS,
          },
          emailNotificationsEnabled && {
            id: 'emailNotificationsTypes',
            preferences: t('common.emailNotificationsTypes'),
            modifySettings: selectedEmailNotificationsTypes,
            modifySettingsProps: {
              isCustomComponent: true,
              CustomComponent: NotificationTypesSelector,
              onChange: handleEmailNotificationsTypesChange,
              options: notificationTypesOptions,
            },
            currentValue: selectedEmailNotificationsTypes.map((v) => v.name).join(', '),
            description: t('common.descriptionEmailNotificationsTypes'),
            group: Groups.EMAIL_NOTIFICATIONS,
          },
          emailNotificationsEnabled && {
            id: 'emailNotificationsDeliveryMode',
            preferences: t('common.emailNotificationsDeliveryMode'),
            modifySettings: selectedEmailNotificationsDeliveryMode,
            modifySettingsProps: {
              onChange: handleEmailNotificationsDeliveryModeChange,
              options: emailNotificationsDeliveryModes,
              placeholder: selectedEmailNotificationsDeliveryMode.name,
              isSearchable: true,
              selectFirstOnSearch: true,
            },
            currentValue: selectedEmailNotificationsDeliveryMode.name,
            description: t('common.descriptionEmailNotificationsDeliveryMode'),
            group: Groups.EMAIL_NOTIFICATIONS,
          },
          emailNotificationsEnabled && {
            id: 'emailNotificationsMarkReadAsDelivered',
            preferences: t('common.emailNotificationsMarkReadAsDelivered'),
            modifySettings: emailNotificationsMarkReadAsDelivered,
            modifySettingsProps: {
              onChange: handleEmailNotificationsMarkReadAsDeliveredChange,
              title: t('common.toggleItem', { item: t('common.emailNotificationsMarkReadAsDelivered', { postProcess: 'lowercase' }) }),
            },
            currentValue: emailNotificationsMarkReadAsDelivered ? t('common.enabled') : t('common.disabled'),
            description: t('common.descriptionEmailNotificationsMarkReadAsDelivered'),
            group: Groups.EMAIL_NOTIFICATIONS,
          },
        ].filter(Boolean),
      [
        t,
        selectedLanguage,
        handleLanguageChange,
        languages,
        Groups.GENERAL,
        Groups.NOTIFICATIONS,
        Groups.EMAIL_NOTIFICATIONS,
        selectedTheme,
        handleThemeChange,
        themes,
        theme,
        selectedThemeShape,
        handleThemeShapeChange,
        themeShapes,
        selectedPreferredDetailsFont,
        handlePreferredDetailsFontChange,
        preferredFonts,
        selectedDefaultView,
        handleDefaultViewChange,
        defaultViews,
        sidebarCompact,
        handleCompactSidebarChange,
        selectedListViewStyle,
        handleListViewStyleChange,
        listStyles,
        selectedUsersSettingsStyle,
        handleUsersSettingsStyleChange,
        hideCardModalActivity,
        handleHideCardModalActivityChange,
        hideClosestDueDate,
        handleHideClosestDueDateChange,
        showFullDueDates,
        handleShowFullDueDatesChange,
        subscribeToOwnCards,
        handleSubscribeToOwnCardsChange,
        subscribeToNewBoards,
        handleSubscribeToNewBoardsChange,
        subscribeToNewProjects,
        handleSubscribeToNewProjectsChange,
        isAdmin,
        subscribeToUsers,
        handleSubscribeToUsersChange,
        subscribeToInstance,
        handleSubscribeToInstanceChange,
        selectedNotificationTypes,
        handleNotificationTypesChange,
        notificationTypesOptions,
        suppressedSystemNotificationTags,
        suppressedSystemNotificationTagsData.text,
        handleSuppressedSystemNotificationTagsFieldChange,
        handleSuppressedSystemNotificationTagsSubmit,
        emailNotificationsEnabled,
        handleEmailNotificationsEnabledChange,
        selectedEmailNotificationsTypes,
        handleEmailNotificationsTypesChange,
        selectedEmailNotificationsDeliveryMode,
        handleEmailNotificationsDeliveryModeChange,
        emailNotificationsDeliveryModes,
        emailNotificationsMarkReadAsDelivered,
        handleEmailNotificationsMarkReadAsDeliveredChange,
      ],
    );

    const table = useReactTable({
      data,
      columns: [],
      initialState: {
        grouping: ['preferences'],
        expanded: true,
      },
      getCoreRowModel: getCoreRowModel(),
      getGroupedRowModel: getGroupedRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
      style: Table.Style.Default,
    });

    const { handleResetColumnWidthsClick } = Table.HooksPre(tableRef, table);

    const resetColumnsWidths = useCallback(() => {
      handleResetColumnWidthsClick(false, true);
    }, [handleResetColumnWidthsClick]);

    useEffect(() => {
      resetColumnsWidths();
    }, [resetColumnsWidths, sidebarCompact]);

    useEffect(() => {
      window.addEventListener('resize', resetColumnsWidths);

      return () => {
        window.removeEventListener('resize', resetColumnsWidths);
      };
    }, [resetColumnsWidths]);

    const columns = useMemo(
      () => [
        {
          accessorKey: 'preferences',
          header: t('common.preferences'),
          cell: Table.Renderers.DefaultCellRenderer,
          enableSorting: false,
          meta: { headerTitle: t('common.preferences') },
          cellProps: { cellClassNameInner: s.cell },
          getGroupingValue: (row) => row.group,
        },
        {
          accessorKey: 'modifySettings',
          header: t('common.modifySettings'),
          cell: Table.Renderers.SettingsCellRenderer,
          enableSorting: false,
          meta: { headerTitle: t('common.modifySettings'), suggestedSize: 200 },
          cellProps: { ariaLabel: t('common.toggleItem', { item: t('common.settings') }) },
        },
        {
          accessorKey: 'currentValue',
          header: t('common.currentValue'),
          cell: Table.Renderers.DefaultCellRenderer,
          enableSorting: false,
          meta: { headerTitle: t('common.currentValue') },
          cellProps: { cellClassNameInner: s.cell },
        },
        {
          accessorKey: 'description',
          header: t('common.description'),
          cell: Table.Renderers.DefaultCellRenderer,
          enableSorting: false,
          meta: { headerTitle: t('common.description') },
          cellProps: { cellClassNameInner: s.cell },
        },
      ],
      [t],
    );

    table.setOptions((prev) => ({ ...prev, columns }));

    return (
      <div className={sShared.wrapper}>
        <div className={sShared.header}>
          <h2 className={sShared.headerText}>{t('common.preferences')}</h2>
        </div>
        <Table.Container className={s.container}>
          <Table.Wrapper className={clsx(gs.scrollableX)}>
            <Table ref={tableRef} style={{ width: `${table.getCenterTotalSize()}px` }}>
              <Table.Header style={Table.Style.Default}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.HeaderRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <Table.HeaderCell
                        key={header.id}
                        style={{ width: `${header.getSize()}px` }}
                        colSpan={header.colSpan}
                        title={header.column.columnDef.meta?.headerTitle}
                        aria-label={header.column.columnDef.meta?.headerAriaLabel}
                        isCentered
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {index + 1 < headerGroup.headers.length && <Table.Resizer />}
                      </Table.HeaderCell>
                    ))}
                  </Table.HeaderRow>
                ))}
              </Table.Header>
              <Table.Body className={clsx(gs.scrollableY)} style={Table.Style.Default}>
                {table.getRowModel().rows.map((row) => {
                  if (row.getIsGrouped()) {
                    let groupLabel = t('common.general');
                    let groupDescription = '';
                    let groupDescriptionStyle = s.groupDescription;

                    if (row.groupingValue === 'notifications') {
                      groupLabel = t('common.notifications');
                    } else if (row.groupingValue === 'emailNotifications') {
                      groupLabel = t('common.emailNotifications');
                      if (!isVerified) {
                        groupDescription = t('common.requiresEmailVerification');
                        groupDescriptionStyle = s.requiresVerifiedEmail;
                      }
                    }

                    return (
                      <Table.Row key={row.id} className={s.groupRow}>
                        <Table.Cell colSpan={columns.length}>
                          <strong>
                            {groupLabel}
                            <span className={groupDescriptionStyle}> {groupDescription}</span>
                          </strong>
                        </Table.Cell>
                      </Table.Row>
                    );
                  }
                  return (
                    <Table.Row key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell key={cell.id} style={{ width: `${cell.column.getSize()}px` }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </Table.Wrapper>
        </Table.Container>
      </div>
    );
  },
);

PreferencesSettings.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  isVerified: PropTypes.bool.isRequired,
  subscribeToOwnCards: PropTypes.bool.isRequired,
  subscribeToNewBoards: PropTypes.bool.isRequired,
  subscribeToNewProjects: PropTypes.bool.isRequired,
  subscribeToUsers: PropTypes.bool.isRequired,
  subscribeToInstance: PropTypes.bool.isRequired,
  sidebarCompact: PropTypes.bool.isRequired,
  language: PropTypes.string,
  defaultView: PropTypes.string.isRequired,
  listViewStyle: PropTypes.string.isRequired,
  usersSettingsStyle: PropTypes.string.isRequired,
  preferredDetailsFont: PropTypes.string.isRequired,
  hideCardModalActivity: PropTypes.bool.isRequired,
  hideClosestDueDate: PropTypes.bool.isRequired,
  showFullDueDates: PropTypes.bool.isRequired,
  theme: PropTypes.string.isRequired,
  themeShape: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  emailNotificationsEnabled: PropTypes.bool.isRequired,
  emailNotificationsTypes: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  emailNotificationsDeliveryMode: PropTypes.string.isRequired,
  emailNotificationsMarkReadAsDelivered: PropTypes.bool.isRequired,
  notificationTypes: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  suppressedSystemNotificationTags: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
};

PreferencesSettings.defaultProps = {
  language: undefined,
};

export default PreferencesSettings;
